import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, RotateCcw, Search, FileText, Loader2, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Closeout Review Page — Order Closeout Phase 1
 * Office reviews inspector-submitted closeouts. Approve / Correct / Follow-Up.
 * HARD LOCK: does not touch Dispatch/Scheduling/Finance/Photos/Reports.
 */
const CloseoutReviewPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("pending");
  const [filterInspector, setFilterInspector] = useState("");
  const [search, setSearch] = useState("");
  const [msg, setMsg] = useState(null);
  const [drawer, setDrawer] = useState(null);        // {closing, mode: approve|correct|follow_up}
  const [audit, setAudit] = useState(null);
  const [reasonList, setReasonList] = useState([]);
  const [statusList, setStatusList] = useState([]);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterInspector) params.set("inspector", filterInspector);
      const [{ data: q }, { data: r }] = await Promise.all([
        axios.get(`${API}/closeout/review-queue?${params.toString()}`),
        axios.get(`${API}/closeout/reasons`),
      ]);
      setItems(q.items || []);
      setReasonList(r.reasons || []);
      setStatusList(r.statuses || []);
    } catch (e) {
      setMsg({ kind: "error", text: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterInspector]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i =>
      String(i.order_number || "").toLowerCase().includes(q) ||
      String(i.inspector_name || "").toLowerCase().includes(q) ||
      String(i.closeout_reason_label || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const openAudit = async (id) => {
    try {
      const { data } = await axios.get(`${API}/closeout/${id}/audit`);
      setAudit(data);
    } catch (e) {
      setMsg({ kind: "error", text: e.response?.data?.detail || "Falha ao carregar log" });
    }
  };

  const runAction = async (payload) => {
    if (!drawer) return;
    try {
      await axios.patch(`${API}/closeout/${drawer.closing.id}/office-action`, payload);
      setMsg({ kind: "success", text: `Ação '${payload.action}' aplicada em ${drawer.closing.order_number}` });
      setDrawer(null);
      fetchQueue();
    } catch (e) {
      setMsg({ kind: "error", text: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="closeout-review-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Closeout Review</h1>
          <p className="text-sm text-gray-500 mt-0.5">Revisar fechamentos enviados pelos inspetores. Aprove, corrija ou marque follow-up.</p>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-2.5 rounded text-sm ${msg.kind === "error" ? "bg-red-50 text-red-800 border border-red-200" : "bg-green-50 text-green-800 border border-green-200"}`}
             data-testid="closeout-msg">
          <div className="flex items-center justify-between">
            <span>{msg.text}</span>
            <button onClick={() => setMsg(null)} className="text-xs opacity-60 hover:opacity-100">✕</button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar ordem / inspetor / motivo..."
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs"
            data-testid="closeout-search" />
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs">
          <Filter className="h-3.5 w-3.5 text-gray-400" />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-xs bg-transparent focus:outline-none" data-testid="closeout-filter-status">
            <option value="pending">Pending review</option>
            <option value="approved">Approved</option>
            <option value="corrected">Corrected</option>
            <option value="follow_up_marked">Follow-Up marked</option>
            <option value="all">All</option>
          </select>
        </div>
        <input value={filterInspector} onChange={e => setFilterInspector(e.target.value)} placeholder="Inspector name filter..."
          className="px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-xs w-56" data-testid="closeout-filter-inspector" />
        <span className="text-[10px] text-gray-400">{filtered.length} of {items.length}</span>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="closeout-table-wrap">
        {loading ? (
          <div className="p-10 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400 text-sm">Nenhum fechamento nessa fila.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase text-gray-500 tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Order #</th>
                <th className="text-left px-4 py-2">Inspector</th>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-left px-4 py-2">Note</th>
                <th className="text-right px-4 py-2">Expected $</th>
                <th className="text-left px-4 py-2">Review</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => (
                <tr key={i.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`closeout-row-${i.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{i.order_number || "—"}</td>
                  <td className="px-4 py-2 text-xs">{i.inspector_name || "—"}</td>
                  <td className="px-4 py-2 text-xs">{i.route_date || "—"}</td>
                  <td className="px-4 py-2">
                    <StatusPill status={i.closeout_status} />
                  </td>
                  <td className="px-4 py-2 text-xs">{i.closeout_reason_label || <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-2 text-xs text-gray-600 max-w-[220px] truncate" title={i.closeout_note || ""}>
                    {i.closeout_note || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right text-xs font-medium">${(i.expected_value || 0).toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <ReviewPill status={i.office_review_status} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setDrawer({ closing: i, mode: "approve" })}
                        className="p-1.5 hover:bg-green-100 rounded text-green-700" title="Approve"
                        data-testid={`closeout-approve-${i.id}`}>
                        <CheckCircle2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDrawer({ closing: i, mode: "correct" })}
                        className="p-1.5 hover:bg-yellow-100 rounded text-yellow-700" title="Correct"
                        data-testid={`closeout-correct-${i.id}`}>
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDrawer({ closing: i, mode: "follow_up" })}
                        className="p-1.5 hover:bg-red-100 rounded text-red-700" title="Mark Follow-Up"
                        data-testid={`closeout-followup-${i.id}`}>
                        <AlertCircle className="h-4 w-4" />
                      </button>
                      <button onClick={() => openAudit(i.id)}
                        className="p-1.5 hover:bg-gray-100 rounded text-gray-500" title="Audit log"
                        data-testid={`closeout-audit-${i.id}`}>
                        <FileText className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawer && (
        <ActionDrawer drawer={drawer} onClose={() => setDrawer(null)} onSubmit={runAction}
          reasons={reasonList} statuses={statusList} />
      )}
      {audit && (
        <AuditDrawer audit={audit} onClose={() => setAudit(null)} />
      )}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    "Complete":               "bg-green-100 text-green-800",
    "Incompleta":             "bg-yellow-100 text-yellow-800",
    "Follow-Up Necessário":   "bg-red-100 text-red-800",
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${map[status] || "bg-gray-100 text-gray-600"}`}>{status || "—"}</span>;
};

const ReviewPill = ({ status }) => {
  const map = {
    pending:            { c: "bg-blue-100 text-blue-800",   label: "Pending" },
    approved:           { c: "bg-green-100 text-green-800", label: "Approved" },
    corrected:          { c: "bg-yellow-100 text-yellow-800", label: "Corrected" },
    follow_up_marked:   { c: "bg-red-100 text-red-800",     label: "Follow-Up" },
  };
  const it = map[status] || { c: "bg-gray-100 text-gray-500", label: status || "—" };
  return <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${it.c}`}>{it.label}</span>;
};

const ActionDrawer = ({ drawer, onClose, onSubmit, reasons, statuses }) => {
  const [newStatus, setNewStatus] = useState(drawer.closing.closeout_status || "Complete");
  const [newReason, setNewReason] = useState(drawer.closing.closeout_reason_code || "");
  const [correctionNote, setCorrectionNote] = useState("");
  const isCorrect = drawer.mode === "correct";
  const isFollowUp = drawer.mode === "follow_up";

  const submit = () => {
    if (drawer.mode === "approve") return onSubmit({ action: "approve" });
    if (drawer.mode === "follow_up") return onSubmit({ action: "follow_up" });
    // correct
    if (!correctionNote.trim()) return alert("Correction note é obrigatório");
    onSubmit({
      action: "correct",
      new_status: newStatus,
      new_reason_code: newReason || null,
      correction_note: correctionNote.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="closeout-action-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <p className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider">
            {drawer.mode === "approve" && "Approve"}
            {drawer.mode === "correct" && "Correct"}
            {drawer.mode === "follow_up" && "Mark Follow-Up"}
          </p>
          <p className="text-sm font-bold text-gray-800">Order {drawer.closing.order_number} · {drawer.closing.inspector_name}</p>
        </div>

        <div className="p-5 space-y-4">
          <div className="p-3 bg-gray-50 rounded border border-gray-100 text-xs">
            <p><span className="text-gray-500">Inspector submitted:</span> <strong>{drawer.closing.closeout_status}</strong></p>
            {drawer.closing.closeout_reason_label && <p><span className="text-gray-500">Reason:</span> {drawer.closing.closeout_reason_label}</p>}
            {drawer.closing.closeout_note && <p><span className="text-gray-500">Note:</span> {drawer.closing.closeout_note}</p>}
            <p><span className="text-gray-500">Expected value:</span> <strong>${(drawer.closing.expected_value || 0).toFixed(2)}</strong></p>
          </div>

          {isCorrect && (
            <>
              <div>
                <label className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider block mb-1">New status</label>
                <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white"
                  data-testid="drawer-new-status">
                  {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider block mb-1">New reason</label>
                <select value={newReason} onChange={e => setNewReason(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white"
                  data-testid="drawer-new-reason">
                  <option value="">(keep current)</option>
                  {reasons.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider block mb-1">Correction note (required)</label>
                <textarea value={correctionNote} onChange={e => setCorrectionNote(e.target.value)}
                  rows={3} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Descreva o que foi corrigido e por quê"
                  data-testid="drawer-correction-note" />
              </div>
            </>
          )}

          {isFollowUp && (
            <div className="p-3 bg-red-50 rounded border border-red-100 text-xs text-red-800">
              Marcar como follow-up vai mudar o closeout_status para <strong>Follow-Up Necessário</strong>.
              O SLA padrão é 7 dias. Na Fase 2 essa ordem entrará na fila de follow-ups pendentes com o valor retido.
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700" data-testid="drawer-cancel-btn">Cancel</button>
          <button onClick={submit} className="px-4 py-1.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-xs font-semibold rounded-lg" data-testid="drawer-submit-btn">
            Confirm {drawer.mode}
          </button>
        </div>
      </div>
    </div>
  );
};

const AuditDrawer = ({ audit, onClose }) => (
  <div className="fixed inset-0 z-50 flex" data-testid="closeout-audit-drawer">
    <div className="flex-1 bg-black/40" onClick={onClose} />
    <div className="w-full max-w-md bg-white shadow-2xl overflow-y-auto">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
        <p className="text-sm font-bold text-gray-800">Audit log · {audit.closing_id.slice(-6)}</p>
        <button onClick={onClose} className="text-xs text-gray-500">✕</button>
      </div>
      <div className="p-5 space-y-2">
        {audit.audit_log.length === 0 ? (
          <p className="text-xs text-gray-400">Sem eventos.</p>
        ) : audit.audit_log.map((e, i) => (
          <div key={i} className="border border-gray-100 rounded p-2.5 bg-gray-50 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-gray-700">{e.event}</span>
              <span className="text-[10px] text-gray-400">{(e.logged_at || "").slice(0, 19).replace("T", " ")}</span>
            </div>
            <p className="text-gray-600">
              {e.from_status || "—"} → <strong>{e.to_status || "—"}</strong>
              <span className="text-gray-400"> · by {e.actor} ({e.actor_role})</span>
            </p>
            {e.reason_code && <p className="text-[10px] text-gray-500 mt-1">reason: {e.reason_code}</p>}
            {e.note && <p className="text-[10px] text-gray-500 mt-1 italic">&ldquo;{e.note}&rdquo;</p>}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default CloseoutReviewPage;
